<!--
  通用文件上传公共组件
  使用示例：
  <CommonUpload
    ref="uploadRef"
    v-model="fileList"
    :uploadUrl="'/common/upload'"
    :accept="'.pdf,.doc,.docx,.jpg,.png'"
    :maxCount="5"
    :maxSize="10"
    :multiple="true"
    :disabled="false"
    :listType="'text'"
    :uploadData="{ bizType: 'degree' }"
    :showUploadList="true"
    :bordered="true"
    :retryable="false"
    :title="'附件上传'"
    :required="true"
    :tipNotes="'注：仅支持 PDF/Word/图片格式，单个最大 10MB'"
    @change="onFileChange"
    @remove="onFileRemove"
    @success="onUploadSuccess"
    @error="onUploadError"
  />

  重置上传列表：
    this.$refs.uploadRef.clearFiles()
    或改变 resetKey 值：this.uploadResetKey = Math.random()

  公共方法（通过 ref 调用）：
    clearFiles()         - 清空文件列表
    getFileList()        - 获取当前文件列表
    getUploadedFiles()   - 获取已上传成功的文件
    getFileUrls()        - 获取所有已上传文件的 URL
    setFileList(list)    - 手动设置文件列表（支持业务数据格式自动转换）
    removeFile(target)   - 移除指定文件（索引或 uid）
    retryUpload(uid)     - 重试失败的上传
    isUploading()        - 是否有文件正在上传中
    validate()           - 校验上传状态

  插槽：
    #trigger   - 自定义上传按钮
    #file-item - 自定义文件列表项渲染（作用域插槽，参数 { fileList }）
    #extra     - 额外提示内容

  图片预览 / 文件下载：
    - picture / picture-card 模式：点击缩略图，图片弹预览、非图片触发下载
    - text 模式：图片文件显示"预览"入口，其他文件显示"下载"入口
    - 支持上传中本地文件 base64 预览 / blob 下载
-->
<template>
  <div class="common-upload" :class="{ 'no-border': !bordered }">
    <!-- 标题区域 -->
    <div class="title-area" v-if="title || !hideTip">
      <span class="required-mark" v-if="required">*</span>
      <div class="title-text" v-if="title">{{ title }}</div>
      <div class="format-tip" v-if="!hideTip">
        {{ tipNotes || defaultTip }}
      </div>
    </div>

    <!-- 说明输入（可选） -->
    <div class="desc-area" v-if="showDescInput">
      <span class="desc-label">说明：</span>
      <a-input v-model="descValue" :placeholder="descPlaceholder" :disabled="disabled" :maxLength="descMaxLength" @input="handleDescInput" class="desc-input" />
    </div>

    <!-- 上传区域 -->
    <div class="upload-action">
      <a-upload
        name="file"
        :headers="headers"
        :action="uploadAction"
        :data="uploadData"
        :list-type="listType"
        :multiple="multiple"
        :show-upload-list="showUploadList"
        :file-list="innerFileList"
        :max-count="maxCount"
        :disabled="disabled"
        :accept="accept"
        :before-upload="beforeUpload"
        :remove="handleRemove"
        @change="handleChange"
        @preview="handlePreview"
        class="uploader"
        :class="{ 'picture-card-uploader': isPictureCard }"
      >
        <!-- 达到最大数量时隐藏上传按钮 -->
        <template v-if="!isMaxReached">
          <!-- 支持自定义上传按钮插槽 -->
          <slot name="trigger">
            <!-- picture-card 模式：使用 antd 默认的加号卡片 -->
            <div v-if="isPictureCard" class="upload-btn-card">
              <a-icon type="plus" />
              <div class="ant-upload-text">{{ displayBtnText }}</div>
            </div>
            <!-- 默认模式：自定义上传按钮 -->
            <div v-else class="upload-btn" :class="{ 'is-disabled': disabled }">
              <img v-if="showIcon" src="@/assets/images/uploadicon.png" alt="upload" class="upload-icon" />
              <span>{{ displayBtnText }}</span>
            </div>
          </slot>
        </template>
      </a-upload>

      <!-- 自定义文件列表项插槽（用于覆盖默认列表渲染） -->
      <slot name="file-item" :fileList="innerFileList"></slot>

      <!-- text 模式下快捷入口：图片显示预览、其他文件显示下载 -->
      <div v-if="listType === 'text'" class="text-preview-list">
        <a v-for="file in fileLinkList" :key="file.uid" class="text-preview-link" @click.prevent="handlePreview(file)">
          <a-icon :type="isImageFile(file) ? 'eye' : 'download'" />
          {{ isImageFile(file) ? '预览' : '下载' }} {{ file.name }}
        </a>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <a-modal :visible="previewVisible" :footer="null" :width="800" @cancel="previewVisible = false">
      <img :src="previewImage" alt="预览图片" style="width: 100%;" />
    </a-modal>

    <!-- 自定义提示插槽 -->
    <slot name="extra"></slot>
  </div>
</template>

<script>
export default {
  name: 'CommonUpload',
  props: {
    // ==================== 核心配置 ====================
    // 文件列表，支持 v-model
    value: {
      type: Array,
      default: () => [],
    },
    // 上传接口地址（不含 baseURL，如 '/common/upload'）
    uploadUrl: {
      type: String,
      default: '/common/upload',
    },
    // 按钮文字
    btnText: {
      type: String,
      default: '选择文件',
    },
    // 外部重置标识，变化时自动清空文件列表（替代 ChangeUpload 的 fileKey + :key 模式）
    resetKey: {
      type: [String, Number],
      default: '',
    },

    // ==================== 上传控制 ====================
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false,
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: true,
    },
    // 接受的文件类型，如 '.pdf,.doc,.jpg' 或 'image/*'
    accept: {
      type: String,
      default: '',
    },
    // 最大上传数量，0 为不限制
    maxCount: {
      type: Number,
      default: 5,
    },
    // 单个文件最大大小（MB）
    maxSize: {
      type: Number,
      default: 10,
    },
    // 上传列表内建样式：'text' | 'picture' | 'picture-card'
    listType: {
      type: String,
      default: 'text',
    },
    // 是否显示上传列表（可传 Boolean 或 Object）
    showUploadList: {
      type: [Boolean, Object],
      default: true,
    },
    // 上传时附带的额外参数（避免使用 data 名称，与 Vue 保留字 data() 区分）
    uploadData: {
      type: Object,
      default: () => ({}),
    },
    // 是否显示底部边框
    bordered: {
      type: Boolean,
      default: true,
    },
    // 是否启用失败重试（在失败文件项上显示重新上传按钮）
    retryable: {
      type: Boolean,
      default: false,
    },

    // ==================== 显示配置 ====================
    // 标题
    title: {
      type: String,
      default: '',
    },
    // 是否必填标记
    required: {
      type: Boolean,
      default: false,
    },
    // 提示文字
    tipNotes: {
      type: String,
      default: '',
    },
    // 是否隐藏提示
    hideTip: {
      type: Boolean,
      default: false,
    },
    // 是否显示上传图标
    showIcon: {
      type: Boolean,
      default: true,
    },

    // ==================== 说明输入配置 ====================
    // 是否显示说明输入框
    showDescInput: {
      type: Boolean,
      default: false,
    },
    // 说明输入占位符
    descPlaceholder: {
      type: String,
      default: '请输入说明',
    },
    // 说明最大长度
    descMaxLength: {
      type: Number,
      default: 200,
    },
    // 说明初始值
    descInitValue: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      innerFileList: [],
      descValue: this.descInitValue,
      _internalChange: false,
      _resetKeyInitialized: false,
      previewVisible: false,
      previewImage: '',
    };
  },
  computed: {
    // 按钮显示文字（picture-card 模式自适应为"上传图片"）
    displayBtnText() {
      return this.isPictureCard && this.btnText === '选择文件' ? '上传图片' : this.btnText;
    },
    // 请求头（computed 确保每次上传获取最新 token）
    headers() {
      return { 'X-Access-Token': localStorage.getItem('token') };
    },
    // 上传接口完整地址
    uploadAction() {
      return `${this.axios.defaults.baseURL}${this.uploadUrl}`;
    },
    // 是否为 picture-card 模式
    isPictureCard() {
      return this.listType === 'picture-card';
    },
    // 是否已达到最大上传数量
    isMaxReached() {
      return this.maxCount > 0 && this.innerFileList.length >= this.maxCount;
    },
    // 已完成且可预览/下载的文件（text 模式快捷入口使用）
    fileLinkList() {
      return this.innerFileList.filter(f => f.status === 'done' && (f.url || f.thumbUrl));
    },
    // 默认提示文字
    defaultTip() {
      const tips = [];
      if (this.accept) {
        tips.push(`支持格式：${this.accept}`);
      }
      if (this.maxCount > 0) {
        tips.push(`最多上传${this.maxCount}个文件`);
      }
      if (this.maxSize > 0) {
        tips.push(`单文件大小上限${this.maxSize}MB`);
      }
      return tips.length ? `注：${tips.join('，')}` : '';
    },
  },
  watch: {
    // 外部 value 变化时同步内部列表（仅处理外部主动变更，忽略内部变更的回传）
    value: {
      handler(val) {
        if (this._internalChange) {
          this._internalChange = false;
          return;
        }
        if (val && Array.isArray(val)) {
          this.innerFileList = val.map(item => this.normalizeFile(item));
        }
      },
      immediate: true,
    },
    // 外部 resetKey 变化时自动清空文件列表（跳过初始化阶段）
    resetKey() {
      if (!this._resetKeyInitialized) {
        this._resetKeyInitialized = true;
        return;
      }
      this.clearFiles();
    },
  },
  methods: {
    /**
     * 将外部文件数据标准化为 a-upload 所需格式
     * @param {Object} item - 文件数据
     * @returns {Object} 标准化后的文件对象
     */
    normalizeFile(item) {
      if (!item) return null;
      // 如果已经是标准 a-upload 格式，直接返回
      if (item.uid && item.status !== undefined) {
        return item;
      }
      // 从业务数据转换
      return {
        uid: item.id || item.uid || `file-${Date.now()}-${Math.random()}`,
        id: item.id,
        status: item.status || 'done',
        size: item.fileSize || item.size,
        name: item.docName || item.name || item.fileName || '未命名文件',
        url: item.docUrl || item.url || item.fileUrl,
        thumbUrl: item.thumbUrl || item.docUrl || item.url,
        remark: item.remark || '',
      };
    },

    /**
     * 上传前校验并将文件加入列表
     * @param {File} file - 待上传文件
     * @returns {Boolean}
     */
    beforeUpload(file) {
      // 文件大小校验
      if (this.maxSize > 0) {
        const isOverSize = file.size / 1024 / 1024 > this.maxSize;
        if (isOverSize) {
          this.$message.error(`只能上传小于${this.maxSize}MB的文件`);
          return false;
        }
      }

      // 数量校验
      if (this.maxCount > 0 && this.innerFileList.length >= this.maxCount) {
        this.$message.error(`最多只能上传${this.maxCount}个文件`);
        return false;
      }

      // 文件格式校验
      if (this.accept) {
        const ext = file.name.substr(file.name.lastIndexOf('.')).toLowerCase();
        const acceptTypes = this.accept.split(',').map(t => t.trim().toLowerCase());
        const isMatchType = acceptTypes.some(type => {
          if (type.startsWith('.')) return ext === type;
          if (type.endsWith('/*')) return file.type.startsWith(type.replace('/*', '/'));
          return file.type === type || ext === type;
        });
        if (!isMatchType) {
          this.$message.error(`上传文件格式不正确，仅支持：${this.accept}`);
          return false;
        }
      }

      // 将文件加入列表（保持与 a-upload 内部状态同步）
      this.innerFileList.push({
        uid: file.uid,
        status: 'uploading',
        size: (file.size / 1024 / 1024).toFixed(2),
        name: file.name,
        url: '',
        percent: 0,
        // 保留原始 File，便于上传中文件也能本地预览 / 下载
        originFileObj: file,
      });
      return true;
    },

    /**
     * 上传状态变化回调
     * @param {Object} info - a-upload change 事件参数
     */
    handleChange(info) {
      const { file } = info;

      // 原地更新文件状态（保持对象引用，确保删除/下载图标正常显示）
      if (file.status === 'done') {
        const index = this.innerFileList.findIndex(item => item.uid === file.uid);
        if (index > -1) {
          if (file.response && file.response.code === 200) {
            this.innerFileList[index].status = 'done';
            const fileUrl = file.response.result || (file.response.data && file.response.data.url) || '';
            this.innerFileList[index].url = fileUrl;
            // 图片文件自动设置 thumbUrl，确保 picture/picture-card 模式缩略图正常显示
            if (this.isImageFile(file)) {
              this.innerFileList[index].thumbUrl = fileUrl;
            }
            this.$message.success(`${file.name} 上传成功`);

            this.$emit('success', {
              file: this.innerFileList[index],
              fileList: [...this.innerFileList],
              response: file.response,
            });
          } else {
            this.innerFileList[index].status = 'error';
            const errMsg = (file.response && file.response.message) || '上传失败';
            this.$message.error(errMsg);

            this.$emit('error', {
              file: this.innerFileList[index],
              fileList: [...this.innerFileList],
              message: errMsg,
            });
          }
        }
      } else if (file.status === 'uploading') {
        const index = this.innerFileList.findIndex(item => item.uid === file.uid);
        if (index > -1) {
          this.innerFileList[index].percent = file.percent;
          if (!this.innerFileList[index].originFileObj && file.originFileObj) {
            this.innerFileList[index].originFileObj = file.originFileObj;
          }
        }
      } else if (file.status === 'error') {
        const index = this.innerFileList.findIndex(item => item.uid === file.uid);
        if (index > -1) {
          this.innerFileList[index].status = 'error';
        }
        this.$message.error(`${file.name} 上传失败`);
        this.$emit('error', {
          file: file,
          fileList: [...this.innerFileList],
          message: '上传失败',
        });
      }

      this.emitChange();
    },

    /**
     * 文件移除回调（直接操作数组，与参考组件一致）
     * @param {Object} file - 被移除的文件
     */
    handleRemove(file) {
      const index = this.innerFileList.findIndex(item => item.uid === file.uid);
      if (index > -1) {
        this.innerFileList.splice(index, 1);
      }
      this.$emit('remove', {
        id: file.id,
        uid: file.uid,
        file,
        fileList: [...this.innerFileList],
      });
      this.emitChange();
    },

    /**
     * 判断是否为图片文件（根据扩展名）
     * @param {Object} file - 文件对象
     * @returns {Boolean}
     */
    isImageFile(file) {
      const name = (file.name || '').toLowerCase();
      return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(name);
    },

    /**
     * 预览入口：仅图片展示预览弹窗，非图片文件触发下载
     * @param {Object} file - 待处理文件
     */
    handlePreview(file) {
      if (this.isImageFile(file)) {
        this.previewImageFile(file);
      } else {
        this.downloadFile(file);
      }
    },

    /**
     * 图片预览（支持 url / thumbUrl / 本地 File 的 base64 预览）
     * @param {Object} file - 待预览的图片文件
     */
    previewImageFile(file) {
      // 优先使用已有 URL
      if (file.url) {
        this.previewImage = file.url;
        this.previewVisible = true;
        return;
      }
      if (file.thumbUrl) {
        this.previewImage = file.thumbUrl;
        this.previewVisible = true;
        return;
      }
      // 本地文件（尚未上传完成）使用 base64 预览
      const raw = file.originFileObj || file;
      if (raw instanceof File) {
        const reader = new FileReader();
        reader.onload = e => {
          this.previewImage = e.target.result;
          this.previewVisible = true;
        };
        reader.readAsDataURL(raw);
      }
    },

    /**
     * 下载文件：非图片类型、或用户主动下载时调用
     * @param {Object} file - 待下载的文件
     */
    downloadFile(file) {
      const url = file.url || file.thumbUrl;

      // 已上传完成，有可访问的 URL
      if (url) {
        const isSameOrigin = url.startsWith('/') || url.startsWith('data:') || url.startsWith('blob:') || url.startsWith(window.location.origin);

        if (isSameOrigin) {
          // 同源/内联数据：直接 a[download] 触发系统下载
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name || '';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          // 跨域资源（如 OSS/CDN）：download 属性不生效，退化为新窗口打开
          window.open(url, '_blank');
        }
        return;
      }

      // 尚未上传完成的本地文件：使用 Blob 下载
      const raw = file.originFileObj || file;
      if (raw instanceof Blob) {
        const blobUrl = URL.createObjectURL(raw);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.name || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // 释放内存
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        return;
      }

      this.$message.warning('暂无可下载的文件');
    },

    /**
     * 重试失败的上传
     * @param {String} uid - 失败文件的 uid
     */
    retryUpload(uid) {
      const file = this.innerFileList.find(item => item.uid === uid && item.status === 'error');
      if (file) {
        file.status = 'uploading';
        file.percent = 0;
      }
    },

    /**
     * 说明输入回调
     */
    handleDescInput() {
      this.$emit('changeDesc', {
        desc: this.descValue,
      });
    },

    /**
     * 触发 change 事件并更新 v-model
     */
    emitChange() {
      const fileList = [...this.innerFileList];
      this._internalChange = true;
      this.$emit('input', fileList);
      this.$emit('change', fileList);
    },

    // ==================== 公共方法（供外部 ref 调用） ====================
    /**
     * 清空文件列表
     */
    clearFiles() {
      this.innerFileList = [];
      this.emitChange();
    },

    /**
     * 获取当前文件列表（返回副本，防止外部直接修改）
     * @returns {Array} 文件列表
     */
    getFileList() {
      return [...this.innerFileList];
    },

    /**
     * 获取已上传成功的文件列表
     * @returns {Array} 上传成功的文件列表
     */
    getUploadedFiles() {
      return this.innerFileList.filter(f => f.status === 'done' && f.url);
    },

    /**
     * 获取所有已上传文件的 URL 列表
     * @returns {Array<string>} URL 数组
     */
    getFileUrls() {
      return this.getUploadedFiles()
        .map(f => f.url)
        .filter(Boolean);
    },

    /**
     * 手动设置文件列表（支持业务数据格式自动转换）
     * @param {Array} list - 文件列表
     */
    setFileList(list) {
      this.innerFileList = (list || []).map(item => this.normalizeFile(item));
      this.emitChange();
    },

    /**
     * 移除指定文件
     * @param {Number|String} target - 文件索引或 uid
     * @returns {Boolean} 是否移除成功
     */
    removeFile(target) {
      let index;
      if (typeof target === 'number') {
        index = target;
      } else {
        index = this.innerFileList.findIndex(item => item.uid === target);
      }
      if (index > -1 && index < this.innerFileList.length) {
        const removed = this.innerFileList.splice(index, 1)[0];
        this.$emit('remove', {
          id: removed.id,
          uid: removed.uid,
          file: removed,
          fileList: [...this.innerFileList],
        });
        this.emitChange();
        return true;
      }
      return false;
    },

    /**
     * 是否有文件正在上传中
     * @returns {Boolean}
     */
    isUploading() {
      return this.innerFileList.some(f => f.status === 'uploading');
    },

    /**
     * 校验上传状态（必填、是否上传中、是否有失败）
     * @returns {Boolean} 校验是否通过
     */
    validate() {
      if (this.required && this.innerFileList.length === 0) {
        this.$message.error(`请上传${this.title || '文件'}`);
        return false;
      }
      if (this.isUploading()) {
        this.$message.warning('文件正在上传中，请稍候');
        return false;
      }
      const hasError = this.innerFileList.some(f => f.status === 'error');
      if (hasError) {
        this.$message.error('存在上传失败的文件，请删除后重新上传');
        return false;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.common-upload {
  font-size: 14px;
  margin-bottom: 2px;
  border-bottom: 1px solid #eee;
  padding: 4px;

  &.no-border {
    border-bottom: none;
  }

  .title-area {
    padding-top: 8px;
    display: flex;
    align-items: center;

    .required-mark {
      color: red;
      margin-right: 4px;
    }

    .title-text {
      // margin: 0 8px;
      font-weight: 500;
    }

    .format-tip {
      padding-left: 8px;
      color: red;
      font-size: 13px;
    }
  }

  .desc-area {
    margin: 8px 0;
    display: flex;
    align-items: center;

    .desc-label {
      margin-left: 13px;
      flex-shrink: 0;
    }

    .desc-input {
      width: 500px;
    }
  }

  .upload-action {
    margin: 12px 0;

    .uploader {
      display: flex;
      flex-direction: column;

      .upload-btn {
        background: #ffffff;
        border-radius: 10px;
        border: 1px solid #e5e9e9;
        height: 80px;
        width: 400px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        transition: all 0.3s;

        &:hover:not(.is-disabled) {
          border-color: #2caf7a;
          background: #f6ffed;
        }

        &.is-disabled {
          cursor: not-allowed;
          opacity: 0.6;
          background: #f5f5f5;
        }

        .upload-icon {
          width: 24px;
          height: 24px;
        }

        span {
          margin-top: 8px;
          color: #333;
        }
      }
    }

    .text-preview-list {
      margin-top: 4px;

      .text-preview-link {
        display: inline-block;
        margin-right: 16px;
        color: #2caf7a;
        cursor: pointer;
        font-size: 13px;

        &:hover {
          color: #279d6a;
        }

        .anticon {
          margin-right: 2px;
        }
      }
    }

    // picture-card 模式样式
    .picture-card-uploader {
      display: block;
    }
  }

  // a-upload 内部列表样式（穿透子组件）
  ::v-deep .ant-upload-list {
    width: 100%;
    max-height: 200px;
    overflow-y: auto;

    .ant-upload-list-item-name {
      color: #2caf7a;
    }

    .ant-upload-list-item-error .ant-upload-list-item-name {
      color: #f5222d;
    }

    .ant-upload-list-item-card-actions {
      .anticon-delete,
      .anticon-download {
        cursor: pointer;
        color: #999;
        transition: color 0.3s;

        &:hover {
          color: #2caf7a;
        }
      }

      .anticon-delete:hover {
        color: #f5222d;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/common/components/CommonUpload.vue